import { React, useState, useEffect } from "react"
import { X, ChevronDown, ChevronUp } from "lucide-react"
import styles from "../../../components/ui/dialog.module.css"
import { ConfirmationModal } from "./ConfirmationModal"
import { ResumeModal } from "./ResumeModal"
import { useUpdateApplicationStatus } from "../../../hooks/DataFetching/useUpdateApplicationStatus"
import { StatusMessage } from "../../ui/StatusMessage"

export const ApplicationsModal = ({
  position,
  applications: initialApplications,
  loading,
  error,
  isOpen,
  onClose,
  onApplicationUpdate,
}) => {
  const [localApplications, setLocalApplications] = useState(initialApplications)
  const [selectedApplication, setSelectedApplication] = useState(null)
  const [confirmationAction, setConfirmationAction] = useState(null)
  const [rejectedCollapsed, setRejectedCollapsed] = useState(true)
  const { updateApplicationStatus, updateLoading, updateError } = useUpdateApplicationStatus()

  useEffect(() => {
    setLocalApplications(initialApplications)
  }, [initialApplications])

  if (!isOpen) return null

  const handleReject = (application) => {
    setSelectedApplication(application)
    setConfirmationAction("reject")
  }

  const handleHire = (application) => {
    setSelectedApplication(application)
    setConfirmationAction("hire")
  }

  const handleConfirmAction = async () => {
    const newStatus = confirmationAction === "hire" ? "Offered" : "Rejected"
    const success = await updateApplicationStatus(selectedApplication.id, newStatus)

    if (success) {
      // Update the local state
      const updatedApplications = localApplications.map((app) =>
        app.id === selectedApplication.id ? { ...app, status: newStatus } : app,
      )

      // Close both modals and update applications
      setConfirmationAction(null)
      setSelectedApplication(null)
      setLocalApplications(updatedApplications)
      if (typeof onApplicationUpdate === "function") {
        onApplicationUpdate(updatedApplications)
      }
    }
  }

  const renderContent = () => {
    if (loading) {
      return (
        <StatusMessage
          tone="loading"
          title="Loading applications"
          message="We are loading the latest applications for this position."
        />
      )
    }

    if (error) {
      return (
        <StatusMessage
          tone="error"
          title="Could not load applications"
          message={error}
        />
      )
    }

    if (!localApplications || localApplications.length === 0) {
      return (
        <StatusMessage
          tone="empty"
          title="No applications found"
          message="No one has applied for this position yet."
        />
      )
    }

    const hasOfferedApplication = localApplications.some((app) => app.status === "Offered")
    const pendingApplications = localApplications.filter((app) => app.status !== "Rejected")
    const rejectedApplications = localApplications.filter((app) => app.status === "Rejected")
    const applicantName = (application) => {
      const user = application?.resume?.user
      const firstName = user?.userFname || user?.userFName || ""
      const lastName = user?.userLname || user?.userLName || ""
      const fullName = `${firstName} ${lastName}`.trim()
      return fullName || application?.userId || "Unknown applicant"
    }

    return (
      <div>
        <ul className="space-y-4">
          {pendingApplications.map((application) => (
            <li key={application.id} className="flex items-center justify-between">
              <div>
                <span className="font-semibold">
                  Applicant: {applicantName(application)}
                </span>
                <span className="ml-2 text-sm text-gray-500">
                  Applied: {new Date(application.applicationDate).toLocaleDateString()}
                </span>
                <span
                  className={`ml-2 text-sm ${application.status === "Offered" ? "text-green-500 font-semibold" : "text-blue-500"}`}
                >
                  Status: {application.status}
                </span>
                <button className="ml-2 text-blue-500 underline" onClick={() => setSelectedApplication(application)}>
                  View Details
                </button>
              </div>
              {!hasOfferedApplication && application.status === "Pending" && (
                <div>
                  <button
                    onClick={() => handleReject(application)}
                    className="bg-red-500 text-white px-2 py-1 rounded mr-2"
                  >
                    Reject
                  </button>
                  <button onClick={() => handleHire(application)} className="bg-green-500 text-white px-2 py-1 rounded">
                    Hire
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
        {rejectedApplications.length > 0 && (
          <div className="mt-6">
            <button
              className="flex items-center text-gray-600 hover:text-gray-800"
              onClick={() => setRejectedCollapsed(!rejectedCollapsed)}
            >
              {rejectedCollapsed ? <ChevronDown className="mr-2" /> : <ChevronUp className="mr-2" />}
              Rejected Applications ({rejectedApplications.length})
            </button>
            {!rejectedCollapsed && (
              <ul className="space-y-4 mt-2">
                {rejectedApplications.map((application) => (
                  <li key={application.id} className="flex items-center justify-between">
                    <div>
                      <span className="font-semibold">
                        Applicant: {applicantName(application)}
                      </span>
                      <span className="ml-2 text-sm text-gray-500">
                        Applied: {new Date(application.applicationDate).toLocaleDateString()}
                      </span>
                      <span className="ml-2 text-sm text-red-500">Status: {application.status}</span>
                      <button
                        className="ml-2 text-blue-500 underline"
                        onClick={() => setSelectedApplication(application)}
                      >
                        View Details
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={styles.dialogOverlay} onClick={onClose}>
      <div className={styles.dialogContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.dialogHeader}>
          <h2 className={styles.dialogTitle}>Applications for {position?.title || "Selected Position"}</h2>
          <button onClick={onClose} className={styles.closeButton} aria-label="Close">
            <X size={24} />
          </button>
        </div>
        <div className={styles.dialogBody}>
          {renderContent()}
          {updateLoading && (
            <StatusMessage
              tone="loading"
              title="Updating application"
              message="We are saving the application status change."
            />
          )}
          {updateError && (
            <StatusMessage
              tone="error"
              title="Could not update application"
              message={updateError}
            />
          )}
        </div>
      </div>
      {selectedApplication && !confirmationAction && (
        <ResumeModal
          application={selectedApplication}
          isOpen={!!selectedApplication}
          onClose={() => setSelectedApplication(null)}
        />
      )}
      {confirmationAction && (
        <ConfirmationModal
          isOpen={!!confirmationAction}
          onClose={() => setConfirmationAction(null)}
          onConfirm={handleConfirmAction}
          action={confirmationAction}
          applicationId={selectedApplication?.id}
        />
      )}
    </div>
  )
}

